import type { PaymentRepository } from 'contracts/PaymentRepositoryContract'
import type * as Brink from 'types/vendors/brink'
import Axios from 'axios'
import { BRINK_API_ORDER_URL } from 'configuration/global.configuration'
import { convertToBrinkAddress, normalizeOrder } from 'lib/brink'

const BLOCKED_PAYMENT_METHODS: string[] = []

export const AdyenBrinkPaymentRepository: PaymentRepository = ({ baseURL, piniaInstance }) => {
  const cartStore = useCartStore(piniaInstance)

  const repository: ReturnType<PaymentRepository> = {
    _name: 'AdyenBrinkPaymentRepository ',
    normalizers: {},
    recentRequests: [],
    api: Axios.create({
      baseURL,
    }),

    async getPaymentMethods({ countryCode, languageCode, channel, amount }) {
      try {
        const { data } = await this.api.post<Brink.PaymentMethodsResponseBody>(
          `/${BRINK_API_ORDER_URL}/payments/adyen/paymentmethods`,
          {
            blockedPaymentMethods: BLOCKED_PAYMENT_METHODS,
            countryCode,
            languageCode,
            channel,
            amount,
          } as Brink.PaymentMethodsRequestBody,
        )
        return data
      }
      catch (e) {
        return Promise.reject(e)
      }
    },

    // ------------------- Session Flow -------------------

    async createPaymentSession({
      shippingAddress,
      billingAddress,
      contact,
      languageCode,
      returnUrl,
    }) {
      try {
        const { data } = await this.api.post<Brink.AdyenSessionResponse>(
          `/${BRINK_API_ORDER_URL}/carts/adyen/orders`,
          {
            blockedPaymentMethods: BLOCKED_PAYMENT_METHODS,
            shippingAddress: convertToBrinkAddress(shippingAddress, contact),
            billingAddress: convertToBrinkAddress(billingAddress, contact),
            email: contact.email,
            languageCode,
            returnUrl,
          } as Brink.AdyenSessionRequestBody,
        )
        const cartStore = useCartStore()
        return {
          order: normalizeOrder(data.brink.order, cartStore.state, cartStore.state.shippingMethod),
          session: data.adyen.session,
        }
      }
      catch (error) {
        if (!Axios.isAxiosError(error))
          return Promise.reject(new Error(`Not axios error${error}`))
        if (error.response)
          return Promise.reject(error.response.data)

        return Promise.reject(error)
      }
    },

    async confirmPaymentSession(signature, orderId) {
      try {
        const { data: order } = await Axios.get<Brink.Order>(
          `${baseURL}/${BRINK_API_ORDER_URL}/payments/adyen/orders/${orderId}/confirmation`,
          {
            headers: {
              authorization: signature,
            },
          },
        )

        if (order.failedReason)
          return Promise.reject(order.failedReason)

        const cartStore = useCartStore()

        // TODO: Does this work?
        return normalizeOrder(order, cartStore.state, cartStore.state.shippingMethod)
      }
      catch (e) {
        return Promise.reject(e)
      }
    },

    // ------------------- Advanced Flow -------------------

    async createCartOrders(body) {
      try {
        const { data } = await this.api.post<Brink.Order>(
          `/${BRINK_API_ORDER_URL}/carts/orders`,
          body || {},
        )

        return data
      }
      catch (e) {
        return Promise.reject(e)
      }
    },

    async requestPayments(body) {
      try {
        const { data } = await this.api.post<Brink.PostAdyenPaymentResponse>(
          `/${BRINK_API_ORDER_URL}/payments/adyen/payments`,
          body,
        )

        return data
      }
      catch (e) {
        return Promise.reject(e)
      }
    },

    async updatePayPalExpressOrder(body) {
      try {
        const { data } = await this.api.post<Brink.AdyenPaymentUpdatePayPalResponse>(
          `/${BRINK_API_ORDER_URL}/payments/adyen/payments/paypal/updateOrder`,
          body,
        )
        return data
      }
      catch (e) {
        return Promise.reject(e)
      }
    },

    async requestPaymentsDetails(body) {
      try {
        const { data } = await this.api.post<Brink.AdyenPaymentDetailsResponse>(
          `/${BRINK_API_ORDER_URL}/payments/adyen/payments/details`,
          body,
        )

        return data
      }
      catch (e) {
        return Promise.reject(e)
      }
    },

  }

  repository.api.interceptors.request.use((config) => {
    if (process.client)
      config.headers.Authorization = cartStore.cartToken

    repository.recentRequests.push({
      method: config.method?.toUpperCase(),
      url: `${baseURL}${Axios.getUri(config)}`,
    })

    return config
  })

  return repository
}
